<!--
 * @Author: Nemo
 * @Description: OnCollapsiblePanelItem组件 - 折叠面板单项组件
 * @Date: 2025-04-28
 * @FilePath: /rhcn-checkout-web/src/componentsV2/base/collapsible-panel/item.vue
-->
<template>
  <div
    class="collapsible-item"
    :class="{
      'is-active': isActive,
      'is-disabled': disabled
    }"
  >
    <div
      class="collapsible-header"
      @click="handleHeaderClick"
      :data-name="name"
    >
      <div class="collapsible-header__left">
        <span class="collapsible-radio"></span>
        <span class="collapsible-icon" v-if="icon">
          <img :src="icon" :alt="title">
        </span>
        <span class="collapsible-title">
          {{ title }}
          <slot name="title"></slot>
        </span>
      </div>
      <div class="collapsible-header__right">
        <slot name="extra"></slot>
        <span class="collapsible-arrow" v-if="showArrow">
          <svg
            class="arrow-icon"
            viewBox="0 0 1024 1024"
            width="12"
            height="12"
          >
            <path d="M500.8 604.778667L267.306667 371.392l-45.226667 45.269333 278.741333 278.613334 278.485334-278.613334-45.248-45.248z" fill="currentColor"></path>
          </svg>
        </span>
      </div>
    </div>
    <div
      class="collapsible-content"
      :style="{
        height: isActive ? contentHeight + 'px' : '0px',
        opacity: isActive ? 1 : 0
      }"
    >
      <div
        class="collapsible-content__inner"
        ref="content"
      >
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OnCollapsiblePanelItem',
  inject: ['collapse'],
  props: {
    // 面板标题
    title: {
      type: String,
      default: '',
    },
    // 面板唯一标识
    name: {
      type: [String, Number],
      required: true,
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false,
    },
    // 图标
    icon: {
      type: String,
      default: '',
    },
    // 是否显示箭头
    showArrow: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      contentHeight: 0,
      resizeObserver: null,
    };
  },
  computed: {
    isActive() {
      return this.collapse.activeNames.includes(this.name);
    },
  },
  watch: {
    isActive: {
      handler(val) {
        if (val) {
          this.$nextTick(this.updateContentHeight);
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.$nextTick(this.updateContentHeight);

    // 监听内容变化以更新高度
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.updateContentHeight);
      this.resizeObserver.observe(this.$refs.content);
    }

    // 监听窗口大小变化
    window.addEventListener('resize', this.updateContentHeight);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateContentHeight);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  methods: {
    handleHeaderClick() {
      if (this.disabled) return;
      this.$emit('header-click', this.name);
      this.collapse.handleItemClick(this.name);
    },
    updateContentHeight() {
      if (this.$refs.content) {
        this.contentHeight = this.$refs.content.scrollHeight;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.collapsible-item {
  margin-bottom: 0;
  border: none;
  border-radius: 0;
  background-color: #fff;
  transition: all 0.3s;
  position: relative;

  &:not(:last-child) {
    border-bottom: 1px solid #D0D5DD;
  }

  &:last-child {
    margin-bottom: 0;
  }

  &.is-active {
    border-color: transparent;
    box-shadow: none;
    background-color: #fff;

    .collapsible-radio {
      border-color: $color-button-primary;

      &::before {
        transform: scale(1);
      }
    }

    .collapsible-arrow {
      transform: rotate(180deg);
    }
  }

  &.is-disabled {
    opacity: 0.6;
    cursor: not-allowed;

    .collapsible-header {
      cursor: not-allowed;
    }
  }
}

.collapsible-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  cursor: pointer;
  user-select: none;
  height: 44px;

  &:hover {
    background-color: #f9fafb;
  }

  &__left {
    display: flex;
    align-items: center;
    flex: 1;
  }

  &__right {
    display: flex;
    align-items: center;
  }
}

.collapsible-radio {
  position: relative;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid #D0D5DD;
  margin-right: 16px;
  flex-shrink: 0;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  background-color: #fff;

  &::before {
    content: '';
    display: block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: $color-button-primary;
    transform: scale(0);
    transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }
}

.collapsible-icon {
  margin-right: 10px;
  flex-shrink: 0;

  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
    vertical-align: middle;
  }
}

.collapsible-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.collapsible-arrow {
  margin-left: 10px;
  color: #909399;
  transition: transform 0.3s;
}

.collapsible-content {
  overflow: hidden;
  transition: height 0.3s cubic-bezier(0.215, 0.61, 0.355, 1),
              opacity 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
}

.collapsible-content__inner {
  padding: 0 10px 10px 10px;
}
</style>
